import { Award, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { Button, NavBar } from '@/components';
import useAuth from '@/hooks/useAuth';

const f11 = [
  {
    title: 'Top 75.000 de la fecha',
    medals: '1 medalla',
  },
  {
    title: 'Top 25.000 de la fecha',
    medals: '2 medallas',
  },
  {
    title: 'Top 10.000 de la fecha',
    medals: '3 medallas',
  },
  {
    title: 'Top 100 de la fecha',
    medals: '5 medallas',
  },
  {
    title: 'Top 75.000 del torneo',
    medals: '4 medallas',
  },
  {
    title: 'Top 25.000 del torneo',
    medals: '8 medallas',
  },
  {
    title: 'Top 10.000 del torneo',
    medals: '12 medallas',
  },
  {
    title: 'Top 100 del torneo',
    medals: '18 medallas',
  },
];

const f5 = [
  {
    title: 'Top 25.000 de la fecha',
    medals: '1 medalla',
  },
  {
    title: 'Top 5.000 de la fecha',
    medals: '3 medallas',
  },
  {
    title: 'Top 100 de la fecha',
    medals: '5 medallas',
  },
  {
    title: 'Top 25.000 del torneo',
    medals: '5 medallas',
  },
  {
    title: 'Top 5.000 del torneo',
    medals: '10 medallas',
  },
  {
    title: 'Top 100 del torneo',
    medals: '15 medallas',
  },
];

const AwardsByChallenge = [
  {
    id: 1,
    title: 'Gran DT Torneo Primera División',
    data: f11,
  },
  {
    id: 2,
    title: 'Gran DT Fútbol 5',
    data: f5,
  },

  {
    id: 3,
    title: 'Trivia',
    data: [
      {
        title: 'Responder 10 preguntas correctamente en 3 minutos',
        medals: '1 medalla',
      },
    ],
  },
  {
    id: 4,
    title: 'Miniligas',
    data: [
      {
        title: 'Campeón de cada Miniliga',
        medals: '5 medallas (máximo 5 medallas por usuario)',
      },
    ],
  },
  {
    id: 5,
    title: 'Fechas Premium',
    data: [{ title: 'Top 1.000', medals: '10 medallas' }],
  },
  {
    id: 6,
    title: 'Desafíos 1 a 1',
    data: [
      {
        title: 'Ganador de 15 desafíos',
        medals: '1 medalla (máximo 5 medallas por usuario)',
      },
    ],
  },
];

const Title = ({ title }: { title: string }) => (
  <div className="flex gap-2">
    <div className="bg-gdt-status-yellow-light w-8 h-8 rounded-full flex justify-center items-center">
      <Award className="text-gdt-status-yellow" size={20} />
    </div>
    <span className="text-lg text-gdt-blue">{title}</span>
  </div>
);

const Accordion = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white mx-6 my-2 rounded-lg">
      <div
        className="flex justify-between cursor-pointer p-6"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-lg  text-gdt-gray-2">{title}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
      {isOpen && <div className="border-t border-gdt-gray-5">{children}</div>}
    </div>
  );
};

const AwardFaq = ({ onNavigateToHowTo }: { onNavigateToHowTo: () => void }) => {
  const { userInitialData } = useAuth();
  return (
    <>
      <div className="bg-white p-6 m-6 rounded-lg">
        <Title title="Premios" />
        <ul className="text-base list-disc space-y-2 mt-8 px-6">
          <li>
            Aquellos Participantes que lleguen a 12 medallas: Serán acreedores
            de un PIN para utilizar en Fútbol 5 de Gran DT Torneo{' '}
            {userInitialData?.torneo}.
          </li>
          <li>
            Aquellos Participantes que lleguen a 20 medallas: Serán acreedores
            de un PIN para utilizar en el equipo de Primera de Gran DT Torneo{' '}
            {userInitialData?.torneo}.
          </li>
          <li>
            Aquellos Participantes que lleguen a 40 medallas: Serán acreedores
            de un Pack Premium para lo que resta de la edición de Gran DT Torneo{' '}
            {userInitialData?.torneo}.
          </li>
          <li>
            Los 5 (cinco) usuarios que más medallas sumen al finalizar la
            edición de Gran DT Torneo {userInitialData?.torneo} ganarán un Smart
            TV cada uno.
          </li>
        </ul>
      </div>

      <div className="flex justify-center mt-2">
        <Button
          className="font-bold"
          type="button"
          variant="text"
          onClick={onNavigateToHowTo}
        >
          ¿Cómo sumo medallas?
        </Button>
      </div>
    </>
  );
};

const HowToGetMedals = ({ onBack }: { onBack: () => void }) => {
  const [showFaqs, setShowFaqs] = useState(false);

  const handleExitFaqs = () => {
    setShowFaqs(false);
  };

  if (showFaqs) {
    return <Faqs handleBackButton={handleExitFaqs} pageToShow={0} />;
  }

  return (
    <>
      <div className="bg-white p-6 m-6 rounded-lg">
        <p className="text-base">
          <strong>
            El Medallero premia tu rendimiento en todas las competencias del
            juego.
          </strong>{' '}
          A mejor rendimiento, tendrás mayor cantidad de medallas. Hay un
          Ranking Medallero, donde se puede ver a los participantes que tienen
          la mayor cantidad de medallas. Los participantes logran sumar medallas
          en función de su desempeño en el juego.
        </p>
      </div>
      <div className="px-6 my-2">
        <Title title="¿Cómo sumo medallas?" />
      </div>
      {AwardsByChallenge.map((award) => (
        <Accordion key={award.id} title={award.title}>
          <ul className="text-base space-y-6 my-4 px-6">
            {award.data.map((data, index) => (
              <li key={award.id + index} className="flex justify-between">
                {data.title} <strong className="text-end">{data.medals}</strong>
              </li>
            ))}
          </ul>
        </Accordion>
      ))}
      <div className="flex justify-center my-2">
        <Button
          className="font-bold"
          type="button"
          variant="text"
          onClick={onBack}
        >
          Premios
        </Button>
      </div>
    </>
  );
};
interface FaqsProps {
  pageToShow: number;
  handleBackButton: () => void;
}
type PageType = 0 | 1; // 0 = howto, 1 = awards

const Faqs = ({ pageToShow, handleBackButton }: FaqsProps) => {
  const [navigationHistory, setNavigationHistory] = useState<PageType[]>([
    pageToShow as PageType,
  ]);

  const currentPage = navigationHistory[navigationHistory.length - 1];

  const navigateTo = (page: PageType) => {
    setNavigationHistory((prev) => [...prev, page]);
  };

  const handleBack = () => {
    if (navigationHistory.length <= 1) {
      handleBackButton();
    } else {
      setNavigationHistory((prev) => prev.slice(0, -1));
    }
  };

  return (
    <NavBar
      mobile={{
        leftContent: {
          showMenuAction: false,
          backAction: handleBack,
        },
        centerContent: {
          title: 'Mis Medallas',
        },
      }}
    >
      {currentPage === 0 ? (
        <HowToGetMedals onBack={() => navigateTo(1)} />
      ) : (
        <AwardFaq onNavigateToHowTo={() => navigateTo(0)} />
      )}
    </NavBar>
  );
};

export default Faqs;
